import React, { useCallback, useEffect, useState } from 'react';
import { usePage } from '../../hooks/usePage';
import {
  shopApi,
  AdminCategory,
  CategoryStoreType,
  AdminSubCategory,
  ProductAttributeDefinition,
  ProductAttributeInputType,
} from '../../api/shop';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { PageHeader } from '../../components/ui/PageHeader';
import { useToast } from '../../hooks/useToast';

// Local form shapes — slug is optional (backend auto-derives from name).
interface CategoryForm {
  name: string;
  slug: string;
  imageUrl: string;
  sortOrder: string;
  isActive: boolean;
  storeType: CategoryStoreType;
}

const STORE_TYPE_LABEL: Record<CategoryStoreType, string> = {
  retail: 'Shops',
  restaurant: 'Restaurants',
};

interface SubCategoryForm {
  name: string;
  slug: string;
  sortOrder: string;
  isActive: boolean;
}

interface AttributeForm {
  categoryId: string;
  subCategoryId: string;
  key: string;
  label: string;
  inputType: ProductAttributeInputType;
  options: string;
  required: boolean;
  aiHint: string;
  sortOrder: string;
  isActive: boolean;
}

const emptyCategoryForm: CategoryForm = {
  name: '',
  slug: '',
  imageUrl: '',
  sortOrder: '0',
  isActive: true,
  storeType: 'retail',
};

const emptySubForm: SubCategoryForm = {
  name: '',
  slug: '',
  sortOrder: '0',
  isActive: true,
};

const emptyAttributeForm: AttributeForm = {
  categoryId: '',
  subCategoryId: '',
  key: '',
  label: '',
  inputType: 'text',
  options: '',
  required: false,
  aiHint: '',
  sortOrder: '0',
  isActive: true,
};

const modalLabelStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--neutral-700)',
  display: 'block',
  marginBottom: 6,
};

const modalSelectStyle: React.CSSProperties = {
  height: 40,
  width: '100%',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
  padding: '0 12px',
  background: 'var(--neutral-100)',
  color: 'var(--neutral-800)',
  outline: 'none',
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  alignItems: 'center',
  fontSize: 13,
  color: 'var(--neutral-700)',
  cursor: 'pointer',
};

export const ShopCategories: React.FC = () => {
  usePage('Shop Categories');
  const toast = useToast();

  const [categories, setCategories] = useState<AdminCategory[]>([]);
  /**
   * Shops' categories or restaurants'. Two lists that never mix: a shop
   * picks from one when it sets up, a restaurant from the other, and only
   * the shop list reaches the marketplace.
   */
  const [storeType, setStoreType] = useState<CategoryStoreType>('retail');
  const typeOf = (cat: AdminCategory): CategoryStoreType => cat.storeType ?? 'retail';
  const shown = categories.filter((cat) => typeOf(cat) === storeType);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Category create/edit modal
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<AdminCategory | null>(
    null,
  );
  const [categoryForm, setCategoryForm] =
    useState<CategoryForm>(emptyCategoryForm);

  // Subcategory create/edit modal
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [subParent, setSubParent] = useState<AdminCategory | null>(null);
  const [editingSub, setEditingSub] = useState<AdminSubCategory | null>(null);
  const [subForm, setSubForm] = useState<SubCategoryForm>(emptySubForm);

  const [attributeModalOpen, setAttributeModalOpen] = useState(false);
  const [attributeParent, setAttributeParent] = useState<AdminCategory | null>(
    null,
  );
  const [editingAttribute, setEditingAttribute] =
    useState<ProductAttributeDefinition | null>(null);
  const [attributeForm, setAttributeForm] =
    useState<AttributeForm>(emptyAttributeForm);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<
    | { kind: 'category'; item: AdminCategory }
    | { kind: 'subcategory'; item: AdminSubCategory }
    | { kind: 'attribute'; item: ProductAttributeDefinition }
    | null
  >(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const data = await shopApi.listCategories();
      setCategories(data ?? []);
    } catch (err) {
      toast.error(err, 'Failed to load categories.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void fetchCategories();
  }, [fetchCategories]);

  // ─── Category modal ─────────────────────────────────────────────────────

  function openCreateCategory() {
    setEditingCategory(null);
    // New categories land in the list being looked at.
    setCategoryForm({ ...emptyCategoryForm, storeType });
    setCategoryModalOpen(true);
  }

  function openEditCategory(cat: AdminCategory) {
    setEditingCategory(cat);
    setCategoryForm({
      name: cat.name,
      slug: cat.slug,
      imageUrl: cat.imageUrl ?? '',
      sortOrder: String(cat.sortOrder ?? 0),
      isActive: cat.isActive,
      storeType: typeOf(cat),
    });
    setCategoryModalOpen(true);
  }

  async function handleSaveCategory() {
    if (!categoryForm.name.trim()) {
      toast.warning('Category name is required.');
      return;
    }
    const payload = {
      name: categoryForm.name.trim(),
      slug: categoryForm.slug.trim() || undefined,
      imageUrl: categoryForm.imageUrl.trim() || null,
      sortOrder: Number(categoryForm.sortOrder) || 0,
      isActive: categoryForm.isActive,
      storeType: categoryForm.storeType,
    };
    try {
      setSaving(true);
      if (editingCategory) {
        await shopApi.updateCategory(editingCategory.id, payload);
        toast.success('Category updated.');
      } else {
        await shopApi.createCategory(payload);
        toast.success('Category created.');
      }
      setCategoryModalOpen(false);
      void fetchCategories();
    } catch (err) {
      toast.error(err, 'Failed to save category.');
    } finally {
      setSaving(false);
    }
  }

  // ─── Subcategory modal ──────────────────────────────────────────────────

  function openCreateSub(parent: AdminCategory) {
    setSubParent(parent);
    setEditingSub(null);
    setSubForm(emptySubForm);
    setSubModalOpen(true);
  }

  function openEditSub(parent: AdminCategory, sub: AdminSubCategory) {
    setSubParent(parent);
    setEditingSub(sub);
    setSubForm({
      name: sub.name,
      slug: sub.slug,
      sortOrder: String(sub.sortOrder ?? 0),
      isActive: sub.isActive,
    });
    setSubModalOpen(true);
  }

  async function handleSaveSub() {
    if (!subParent) return;
    if (!subForm.name.trim()) {
      toast.warning('Subcategory name is required.');
      return;
    }
    const payload = {
      name: subForm.name.trim(),
      slug: subForm.slug.trim() || undefined,
      sortOrder: Number(subForm.sortOrder) || 0,
      isActive: subForm.isActive,
    };
    try {
      setSaving(true);
      if (editingSub) {
        await shopApi.updateSubCategory(editingSub.id, payload);
        toast.success('Subcategory updated.');
      } else {
        await shopApi.createSubCategory(subParent.id, payload);
        toast.success('Subcategory created.');
      }
      setSubModalOpen(false);
      void fetchCategories();
    } catch (err) {
      toast.error(err, 'Failed to save subcategory.');
    } finally {
      setSaving(false);
    }
  }

  // ─── Attribute modal ────────────────────────────────────────────────────

  function openCreateAttribute(parent: AdminCategory) {
    setAttributeParent(parent);
    setEditingAttribute(null);
    setAttributeForm({
      ...emptyAttributeForm,
      categoryId: parent.id,
    });
    setAttributeModalOpen(true);
  }

  function openEditAttribute(
    parent: AdminCategory,
    attribute: ProductAttributeDefinition,
  ) {
    setAttributeParent(parent);
    setEditingAttribute(attribute);
    setAttributeForm({
      categoryId: attribute.categoryId,
      subCategoryId: attribute.subCategoryId ?? '',
      key: attribute.key,
      label: attribute.label,
      inputType: attribute.inputType,
      options: attribute.options.join(', '),
      required: attribute.required,
      aiHint: attribute.aiHint ?? '',
      sortOrder: String(attribute.sortOrder ?? 0),
      isActive: attribute.isActive,
    });
    setAttributeModalOpen(true);
  }

  async function handleSaveAttribute() {
    if (!attributeParent) return;
    if (!attributeForm.label.trim() || !attributeForm.key.trim()) {
      toast.warning('Attribute label and key are required.');
      return;
    }
    const payload = {
      categoryId: attributeForm.categoryId,
      subCategoryId: attributeForm.subCategoryId || null,
      key: attributeForm.key.trim(),
      label: attributeForm.label.trim(),
      inputType: attributeForm.inputType,
      options: attributeForm.options
        .split(',')
        .map((option) => option.trim())
        .filter(Boolean),
      required: attributeForm.required,
      aiHint: attributeForm.aiHint.trim() || null,
      sortOrder: Number(attributeForm.sortOrder) || 0,
      isActive: attributeForm.isActive,
    };
    try {
      setSaving(true);
      if (editingAttribute) {
        await shopApi.updateAttributeDefinition(editingAttribute.id, payload);
        toast.success('Product detail updated.');
      } else {
        await shopApi.createAttributeDefinition(payload);
        toast.success('Product detail created.');
      }
      setAttributeModalOpen(false);
      void fetchCategories();
    } catch (err) {
      toast.error(err, 'Failed to save product detail.');
    } finally {
      setSaving(false);
    }
  }

  // ─── Delete ─────────────────────────────────────────────────────────────

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      if (deleteTarget.kind === 'category') {
        await shopApi.deleteCategory(deleteTarget.item.id);
        toast.success('Category deleted.');
      } else if (deleteTarget.kind === 'subcategory') {
        await shopApi.deleteSubCategory(deleteTarget.item.id);
        toast.success('Subcategory deleted.');
      } else {
        await shopApi.deleteAttributeDefinition(deleteTarget.item.id);
        toast.success('Product detail deleted.');
      }
      setDeleteTarget(null);
      void fetchCategories();
    } catch (err) {
      toast.error(err, 'Failed to delete.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Shop Categories"
        description="Shop categories are the marketplace taxonomy: what vendors and shops choose from when listing products, and what buyers browse on mobile. Restaurant categories are what a restaurant picks when it sets up, and never appear in the marketplace."
        actions={
          <Button onClick={openCreateCategory}>+ New Category</Button>
        }
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }} role="tablist">
        {(['retail', 'restaurant'] as const).map((type) => (
          <Button
            key={type}
            role="tab"
            aria-selected={storeType === type}
            variant={storeType === type ? 'primary' : 'secondary'}
            onClick={() => setStoreType(type)}
          >
            {STORE_TYPE_LABEL[type]} (
            {categories.filter((cat) => typeOf(cat) === type).length})
          </Button>
        ))}
      </div>

      {loading ? (
        <Card>
          <div
            style={{ padding: 48, display: 'flex', justifyContent: 'center' }}
          >
            <Spinner />
          </div>
        </Card>
      ) : shown.length === 0 ? (
        <Card>
          <p style={{ color: 'var(--neutral-600)', padding: 24, margin: 0 }}>
            No {STORE_TYPE_LABEL[storeType].toLowerCase()} categories yet. Create one to get started.
          </p>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {shown.map((cat) => (
            <Card key={cat.id}>
              {/* Category header row */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                {cat.imageUrl ? (
                  <img
                    src={cat.imageUrl}
                    alt={cat.name}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 'var(--radius-sm)',
                      objectFit: 'cover',
                      background: 'var(--neutral-200)',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 'var(--radius-sm)',
                      background: 'var(--neutral-200)',
                    }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{cat.name}</span>
                    {!cat.isActive && (
                      <Badge variant="neutral">Hidden</Badge>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--neutral-500)',
                      marginTop: 2,
                    }}
                  >
                    {cat.slug} · order {cat.sortOrder}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Button
                    variant="secondary"
                    onClick={() => openCreateAttribute(cat)}
                  >
                    + Product detail
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => openCreateSub(cat)}
                  >
                    + Subcategory
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => openEditCategory(cat)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setDeleteTarget({ kind: 'category', item: cat })
                    }
                  >
                    Delete
                  </Button>
                </div>
              </div>

              {/* Subcategories */}
              {cat.subCategories.length > 0 && (
                <div
                  style={{
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: '1px solid var(--neutral-200)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  {cat.subCategories.map((sub) => (
                    <div
                      key={sub.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        paddingLeft: 52,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 120 }}>
                        <span style={{ fontSize: 14 }}>{sub.name}</span>
                        {!sub.isActive && (
                          <Badge variant="neutral">Hidden</Badge>
                        )}
                        <span
                          style={{
                            fontSize: 12,
                            color: 'var(--neutral-500)',
                            marginLeft: 8,
                          }}
                        >
                          {sub.slug} · order {sub.sortOrder}
                        </span>
                      </div>
                      <Button
                        variant="secondary"
                        onClick={() => openEditSub(cat, sub)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() =>
                          setDeleteTarget({ kind: 'subcategory', item: sub })
                        }
                      >
                        Delete
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {cat.attributeDefinitions.length > 0 && (
                <div
                  style={{
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: '1px solid var(--neutral-200)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  {cat.attributeDefinitions.map((attr) => {
                    const sub = cat.subCategories.find(
                      (item) => item.id === attr.subCategoryId,
                    );
                    return (
                      <div
                        key={attr.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          paddingLeft: 52,
                          flexWrap: 'wrap',
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 180 }}>
                          <span style={{ fontSize: 14, fontWeight: 500 }}>
                            {attr.label}
                          </span>
                          {attr.required && (
                            <Badge variant="warning">Required</Badge>
                          )}
                          {!attr.isActive && (
                            <Badge variant="neutral">Hidden</Badge>
                          )}
                          <span
                            style={{
                              fontSize: 12,
                              color: 'var(--neutral-500)',
                              marginLeft: 8,
                            }}
                          >
                            {attr.key} · {attr.inputType}
                            {sub ? ` · ${sub.name}` : ' · all subcategories'}
                          </span>
                        </div>
                        <Button
                          variant="secondary"
                          onClick={() => openEditAttribute(cat, attr)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() =>
                            setDeleteTarget({ kind: 'attribute', item: attr })
                          }
                        >
                          Delete
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Category create/edit modal */}
      <Modal
        isOpen={categoryModalOpen}
        onClose={() => (saving ? null : setCategoryModalOpen(false))}
        title={editingCategory ? 'Edit category' : 'New category'}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            label="Name *"
            placeholder={categoryForm.storeType === 'restaurant' ? 'e.g. Grills, suya & BBQ' : 'e.g. Phones'}
            value={categoryForm.name}
            onChange={(e) =>
              setCategoryForm((f) => ({ ...f, name: e.target.value }))
            }
          />
          <div>
            <label style={modalLabelStyle}>For</label>
            <select
              value={categoryForm.storeType}
              onChange={(e) =>
                setCategoryForm((f) => ({
                  ...f,
                  storeType: e.target.value as CategoryStoreType,
                }))
              }
              style={modalSelectStyle}
            >
              <option value="retail">Shops — the marketplace taxonomy</option>
              <option value="restaurant">Restaurants — picked at restaurant setup</option>
            </select>
            {editingCategory && typeOf(editingCategory) !== categoryForm.storeType ? (
              <p style={{ fontSize: 12, color: 'var(--neutral-600)', margin: '6px 0 0' }}>
                Only possible while no store uses this category.
              </p>
            ) : null}
          </div>
          <Input
            label="Slug"
            placeholder="Auto-generated from name if left blank"
            value={categoryForm.slug}
            onChange={(e) =>
              setCategoryForm((f) => ({ ...f, slug: e.target.value }))
            }
            hint="Lowercase URL-safe key. Leave blank to derive from the name."
          />
          <Input
            label="Image URL"
            placeholder="https://…"
            value={categoryForm.imageUrl}
            onChange={(e) =>
              setCategoryForm((f) => ({ ...f, imageUrl: e.target.value }))
            }
          />
          <Input
            label="Sort order"
            type="number"
            value={categoryForm.sortOrder}
            onChange={(e) =>
              setCategoryForm((f) => ({ ...f, sortOrder: e.target.value }))
            }
          />
          <label
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              fontSize: 13,
              color: 'var(--neutral-700)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={categoryForm.isActive}
              onChange={(e) =>
                setCategoryForm((f) => ({ ...f, isActive: e.target.checked }))
              }
            />
            {categoryForm.storeType === 'restaurant'
              ? 'Active (offered to restaurants at setup)'
              : 'Active (visible to buyers on mobile)'}
          </label>
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              marginTop: 8,
            }}
          >
            <Button
              variant="secondary"
              onClick={() => setCategoryModalOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveCategory} disabled={saving}>
              {saving
                ? 'Saving…'
                : editingCategory
                  ? 'Save changes'
                  : 'Create category'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Subcategory create/edit modal */}
      <Modal
        isOpen={subModalOpen}
        onClose={() => (saving ? null : setSubModalOpen(false))}
        title={
          editingSub
            ? `Edit subcategory · ${subParent?.name ?? ''}`
            : `New subcategory · ${subParent?.name ?? ''}`
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            label="Name *"
            placeholder="e.g. iPhone"
            value={subForm.name}
            onChange={(e) =>
              setSubForm((f) => ({ ...f, name: e.target.value }))
            }
          />
          <Input
            label="Slug"
            placeholder="Auto-generated from name if left blank"
            value={subForm.slug}
            onChange={(e) =>
              setSubForm((f) => ({ ...f, slug: e.target.value }))
            }
            hint="Unique within this category. Leave blank to derive from the name."
          />
          <Input
            label="Sort order"
            type="number"
            value={subForm.sortOrder}
            onChange={(e) =>
              setSubForm((f) => ({ ...f, sortOrder: e.target.value }))
            }
          />
          <label
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              fontSize: 13,
              color: 'var(--neutral-700)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={subForm.isActive}
              onChange={(e) =>
                setSubForm((f) => ({ ...f, isActive: e.target.checked }))
              }
            />
            Active (selectable + visible as a filter)
          </label>
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              marginTop: 8,
            }}
          >
            <Button
              variant="secondary"
              onClick={() => setSubModalOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveSub} disabled={saving}>
              {saving
                ? 'Saving…'
                : editingSub
                  ? 'Save changes'
                  : 'Create subcategory'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={attributeModalOpen}
        onClose={() => (saving ? null : setAttributeModalOpen(false))}
        title={
          editingAttribute
            ? `Edit product detail · ${attributeParent?.name ?? ''}`
            : `New product detail · ${attributeParent?.name ?? ''}`
        }
        maxWidth={560}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            label="Label *"
            placeholder="e.g. RAM"
            value={attributeForm.label}
            onChange={(e) =>
              setAttributeForm((f) => ({ ...f, label: e.target.value }))
            }
          />
          <Input
            label="Key *"
            placeholder="e.g. ram"
            value={attributeForm.key}
            onChange={(e) =>
              setAttributeForm((f) => ({ ...f, key: e.target.value }))
            }
            hint="Stable field key used by products. Example: batteryHealth."
          />
          <div>
            <label style={modalLabelStyle}>Subcategory scope</label>
            <select
              value={attributeForm.subCategoryId}
              onChange={(e) =>
                setAttributeForm((f) => ({
                  ...f,
                  subCategoryId: e.target.value,
                }))
              }
              style={modalSelectStyle}
            >
              <option value="">All subcategories</option>
              {(attributeParent?.subCategories ?? []).map((sub) => (
                <option key={sub.id} value={sub.id}>
                  {sub.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={modalLabelStyle}>Input type</label>
            <select
              value={attributeForm.inputType}
              onChange={(e) =>
                setAttributeForm((f) => ({
                  ...f,
                  inputType: e.target.value as ProductAttributeInputType,
                }))
              }
              style={modalSelectStyle}
            >
              <option value="text">Text</option>
              <option value="number">Number</option>
              <option value="select">Select</option>
              <option value="multiselect">Multiselect</option>
              <option value="boolean">Boolean</option>
              <option value="date">Date</option>
            </select>
          </div>
          {(attributeForm.inputType === 'select' ||
            attributeForm.inputType === 'multiselect') && (
            <Input
              label="Options"
              placeholder="New, Used, Refurbished"
              value={attributeForm.options}
              onChange={(e) =>
                setAttributeForm((f) => ({ ...f, options: e.target.value }))
              }
              hint="Comma-separated options."
            />
          )}
          <Input
            label="AI hint"
            placeholder="Explain what this field means to the AI"
            value={attributeForm.aiHint}
            onChange={(e) =>
              setAttributeForm((f) => ({ ...f, aiHint: e.target.value }))
            }
          />
          <Input
            label="Sort order"
            type="number"
            value={attributeForm.sortOrder}
            onChange={(e) =>
              setAttributeForm((f) => ({ ...f, sortOrder: e.target.value }))
            }
          />
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                checked={attributeForm.required}
                onChange={(e) =>
                  setAttributeForm((f) => ({
                    ...f,
                    required: e.target.checked,
                  }))
                }
              />
              Required before publishing
            </label>
            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                checked={attributeForm.isActive}
                onChange={(e) =>
                  setAttributeForm((f) => ({
                    ...f,
                    isActive: e.target.checked,
                  }))
                }
              />
              Active
            </label>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              marginTop: 8,
            }}
          >
            <Button
              variant="secondary"
              onClick={() => setAttributeModalOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveAttribute} disabled={saving}>
              {saving
                ? 'Saving…'
                : editingAttribute
                  ? 'Save changes'
                  : 'Create detail'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => (deleting ? null : setDeleteTarget(null))}
        title={
          deleteTarget?.kind === 'category'
            ? 'Delete category?'
            : deleteTarget?.kind === 'subcategory'
              ? 'Delete subcategory?'
              : 'Delete product detail?'
        }
      >
        {deleteTarget ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ color: 'var(--neutral-700)', margin: 0 }}>
              {deleteTarget.kind === 'category' ? (
                <>
                  Delete <strong>{deleteTarget.item.name}</strong>? Its
                  subcategories are removed too. Products in this category stay,
                  but lose their category assignment.
                </>
              ) : deleteTarget.kind === 'subcategory' ? (
                <>
                  Delete <strong>{deleteTarget.item.name}</strong>? Products
                  tagged with this subcategory keep existing but lose the
                  subcategory tag.
                </>
              ) : (
                <>
                  Delete <strong>{deleteTarget.item.label}</strong>? Existing
                  product values stay stored, but vendors will no longer see
                  this field.
                </>
              )}
            </p>
            <div
              style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}
            >
              <Button
                variant="secondary"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </>
  );
};
